import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function ItemForm({ initial, onSubmit, submitLabel = "Save" }) {
  const [form, setForm] = useState(
    initial || { item_name: "", category: "", metal: "Gold", purity: "22K", hsn: "7113", unit: "gm", active: true }
  );
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
      data-testid="item-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Item Name *</Label>
          <Input required value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} data-testid="item-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Category</Label>
          <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Ring / Bangle / Chain…" />
        </div>
        <div className="space-y-2">
          <Label>Metal</Label>
          <Input value={form.metal} onChange={(e) => setForm({ ...form, metal: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Purity</Label>
          <Input value={form.purity} onChange={(e) => setForm({ ...form, purity: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>HSN Code</Label>
          <Input value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Unit</Label>
          <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
        </div>
      </div>
      <DialogFooter>
        <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" data-testid="item-form-submit">
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function VoucherItems() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = async (search = "") => {
    const { data } = await api.get("/voucher/items", { params: search ? { q: search } : {} });
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const onCreate = async (form) => {
    try {
      const { data } = await api.post("/voucher/items", form);
      toast.success("Item added");
      setOpen(false);
      setItems([data, ...items]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add item");
    }
  };
  const onUpdate = async (form) => {
    try {
      const { data } = await api.put(`/voucher/items/${editTarget.id}`, form);
      toast.success("Item updated");
      setEditTarget(null);
      setItems(items.map((c) => (c.id === data.id ? data : c)));
    } catch {
      toast.error("Could not update");
    }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/voucher/items/${id}`);
      setItems(items.filter((c) => c.id !== id));
      toast.success("Item deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-zinc-500">Voucher ERP</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Item Master</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-zinc-900 hover:bg-zinc-800" data-testid="add-item-button">
              <Plus className="w-4 h-4 mr-1.5" /> Add Item
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>New Item</DialogTitle>
            </DialogHeader>
            <ItemForm onSubmit={onCreate} submitLabel="Add Item" />
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
        <Input
          placeholder="Search by name, HSN, category…"
          value={q}
          onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
          className="pl-9"
          data-testid="item-search-input"
        />
      </div>

      <Card className="p-0 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No items yet.</div>
        ) : (
          <div className="divide-y divide-border" data-testid="item-list">
            {/* Desktop table header */}
            <div className="hidden md:grid grid-cols-[1fr_120px_100px_120px_auto] items-center gap-3 p-3 bg-zinc-50 text-xs text-zinc-500 overline font-semibold">
              <div>Name / Category</div>
              <div>Metal · Purity</div>
              <div>HSN</div>
              <div>Unit</div>
              <div className="text-right">Actions</div>
            </div>
            {/* Mobile card view */}
            <div className="md:hidden divide-y divide-border">
              {items.map((c) => (
                <div key={c.id} className="p-3 hover:bg-zinc-50" data-testid={`item-row-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.item_name}</div>
                    <div className="text-xs text-zinc-500 truncate">{c.category || "—"}</div>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
                    <div className="text-sm text-zinc-600">
                      <span className="font-medium">{c.metal || "—"} · {c.purity || "—"}</span>
                      <span className="mx-2 text-zinc-400">|</span>
                      <span className="font-mono num">{c.hsn || "—"}</span>
                      <span className="mx-2 text-zinc-400">|</span>
                      <span>{c.unit || "gm"}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditTarget(c)} data-testid={`edit-item-${c.id}`}>
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                      {isAdmin && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => onDelete(c.id)} data-testid={`delete-item-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop table view */}
            <div className="hidden md:block">
              {items.map((c) => (
                <div key={c.id} className="grid grid-cols-[1fr_120px_100px_120px_auto] items-center gap-3 p-3 hover:bg-zinc-50" data-testid={`item-row-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{c.item_name}</div>
                    <div className="text-xs text-zinc-500 truncate">{c.category || "—"}</div>
                  </div>
                  <div className="text-sm">{c.metal || "—"} · {c.purity || "—"}</div>
                  <div className="text-sm num">{c.hsn || "—"}</div>
                  <div className="text-sm">{c.unit || "gm"}</div>
                  <div className="flex items-center gap-1 justify-end">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditTarget(c)} data-testid={`edit-item-${c.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {isAdmin && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => onDelete(c.id)} data-testid={`delete-item-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Item</DialogTitle>
          </DialogHeader>
          {editTarget && <ItemForm initial={editTarget} onSubmit={onUpdate} submitLabel="Save Changes" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
